import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime, type ChannelStatus } from './integrationHelpers';
import type { MetaTone } from './IntegrationCard';

export interface MetaLine {
    text: string;
    tone: MetaTone;
}

/**
 * Turns a derived status into the single meta line of a card or a status strip:
 * "Checked 2 hours ago · OK", "Never checked", the failure message, or what is missing.
 */
export function useMetaLine(): (status: ChannelStatus) => MetaLine {
    const { t, i18n } = useTranslation('crm');

    return useCallback(
        (status: ChannelStatus): MetaLine => {
            const row = status.row;
            switch (status.reason) {
                case 'none':
                    return { text: t('integrations.meta.none'), tone: 'muted' };
                case 'saved_not_enabled':
                    return { text: t('integrations.meta.savedNotEnabled'), tone: 'muted' };
                case 'check_failed':
                    return {
                        text: row?.last_check_message || t('integrations.meta.checkFailed'),
                        tone: 'error',
                    };
                case 'ok': {
                    const when = formatRelativeTime(row?.last_checked_at, i18n.language);
                    return {
                        text: when
                            ? t('integrations.meta.checked', { when })
                            : t('integrations.meta.neverChecked'),
                        tone: 'ok',
                    };
                }
                case 'never_checked':
                    return { text: t('integrations.meta.neverChecked'), tone: 'muted' };
                case 'not_configured':
                    return { text: t('integrations.meta.facebook.notConfigured'), tone: 'muted' };
                case 'no_token':
                    return { text: t('integrations.meta.facebook.noToken'), tone: 'muted' };
                case 'needs_reauth':
                    return { text: t('integrations.meta.facebook.needsReauth'), tone: 'error' };
                case 'missing_required':
                    return {
                        text: t('integrations.meta.facebook.missingRequired', {
                            permissions: (status.missing ?? []).join(', '),
                        }),
                        tone: 'error',
                    };
                case 'missing_recommended':
                    return {
                        text: t('integrations.meta.facebook.missingRecommended', {
                            permissions: (status.missing ?? []).join(', '),
                        }),
                        tone: 'warning',
                    };
                default:
                    return { text: '', tone: 'muted' };
            }
        },
        [i18n.language, t]
    );
}
